import { PINNED_BAR_DIALOG_PIN_PX, PINNED_BAR_PILL_REACH_PX } from '@/nova/components/PinnedBar';
import { HEADER_DESKTOP_PX, HEADER_MOBILE_PX } from '@/nova/theme/tokens';
import { scrollParentOf } from '../blog-editor/hooks/useSortableRows';

/**
 * Screen geometry the Creatives game list works around. The layout chrome is fixed over the edges of the
 * screen (the top bar; on phones also the bottom nav pill, its height plus its inset), and the list's live
 * filter pins below it: on desktop also below PageShell's header scrim band (the 88px header plus its 24px
 * content gap), which would otherwise paint over it.
 */
export type ScreenInsets = { top: number; bottom: number };

/**
 * Where a game list is shown: on the page (under the layout chrome, scrolling with the document) or in a
 * dialog (scrolling inside the dialog's own scroll area, where the filter pins to that area's top).
 */
export type ListPlacement = 'page' | 'dialog';

/** The Games tab on a wide desktop: the page tree, the open page's lists, and the open list's games, which take the
 *  rest but need GAME_LISTS_MAIN_MIN_PX of it, each 24px from the next. Narrower than all three (and on a phone) the
 *  tab drills down instead: the pages, then a page's lists, then a list's games, one at a time. */
export const GAME_LISTS_TREE_PX = 232;
export const GAME_LISTS_LISTS_PX = 280;
const GAME_LISTS_MAIN_MIN_PX = 480;
export const GAME_LISTS_GAP_PX = 24;
/** The three columns' least height, where the screen is too short for more. */
export const GAME_LISTS_PANES_MIN_PX = 360;
export const GAME_LISTS_COLUMNS_MIN_PX =
  GAME_LISTS_TREE_PX + GAME_LISTS_LISTS_PX + GAME_LISTS_MAIN_MIN_PX + 2 * GAME_LISTS_GAP_PX;
/** The open list's column wide enough for its search (200px at least) beside its actions with their labels (Preview on
 *  the site's the longest, in Spanish): about 580px of content. Narrower, the actions draw their icons alone, as on a
 *  phone, so the row holds them. */
const GAME_LISTS_MAIN_LABELLED_PX = 640;
export const GAME_LISTS_LABELLED_ACTIONS_MIN_PX =
  GAME_LISTS_TREE_PX + GAME_LISTS_LISTS_PX + GAME_LISTS_MAIN_LABELLED_PX + 2 * GAME_LISTS_GAP_PX;

/** The area `node` scrolls in (the studio's scroller on a desktop), whether or not it has anything to scroll right now:
 *  null for the document. (`scrollParentOf` only answers while the area overflows.) */
export const scrollAreaOf = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return null;
};

export const SCREEN_CHROME: Record<'desktop' | 'phone', ScreenInsets> = {
  desktop: { top: HEADER_DESKTOP_PX, bottom: 0 },
  phone: { top: HEADER_MOBILE_PX, bottom: 110 },
};

/** Where the floating filter pins. */
export const FILTER_PIN_TOP = { desktop: HEADER_DESKTOP_PX + 24, phone: HEADER_MOBILE_PX + 8 };

/** How far under what is pinned above it (the top bar, or the area header and its fade tail) the row pins on a phone:
 *  its field then stands as far under the header's name as the list stands under the row (the user, 2026-10-21). Held,
 *  the row is no pill there but a blurring layer, which reaches up under what is above it. */
const PHONE_ROW_UNDER_PX = 5;

/** Where the filter's row pins on the page when it carries the list's actions (Game Lists). Held there, it lifts into
 *  the Stats header's pill (PinnedBar), which reaches 10px above its bar. On desktop the pill's top is 22px higher than
 *  the floating filter, closer under the top bar (which closes the 2px gap that was left between the row and the bar
 *  above it). On a phone it pins just under what is above it (PHONE_ROW_UNDER_PX). */
export const ACTIONS_ROW_PIN_TOP = {
  desktop: FILTER_PIN_TOP.desktop - 22 + PINNED_BAR_PILL_REACH_PX,
  phone: HEADER_MOBILE_PX + PHONE_ROW_UNDER_PX,
};

/** The filter field's height: the column of lists' search's (PageTree's SearchInput), which it stands level with. */
export const FILTER_BAR_PX = 40;

/** The gap the list keeps under the row carrying its actions, on the page: GameListGrid's Wrap gap (SPACE[24]), which
 *  it must equal. */
const ROW_LIST_GAP_PX = 24;

/** The top of the screen the pinned filter covers, down to where a scrolled-to match lands: the bottom of the row
 *  carrying the list's actions and the gap the list keeps under it at rest, clear of the row's pill. A floating
 *  filter's match lands 12px under it on a desktop, 66px on a phone. */
export const FILTER_CLEARANCE = {
  desktop: ACTIONS_ROW_PIN_TOP.desktop + FILTER_BAR_PX + ROW_LIST_GAP_PX,
  phone: ACTIONS_ROW_PIN_TOP.phone + FILTER_BAR_PX + ROW_LIST_GAP_PX,
};

/** In a dialog the filter's row is a band either side of its bar. Off the scale on purpose: the band the row had before
 *  it pinned in a pill, kept so the sheet's spacing at rest is unchanged. */
export const DIALOG_BAND_PX = 6;

/** In a dialog the filter's row pins near the top of the scroll area (the room of its pill above it); a match lands
 *  just below the pill, which reaches a pill's reach below the bar, past the band. */
export const DIALOG_FILTER_CLEARANCE =
  PINNED_BAR_DIALOG_PIN_PX + DIALOG_BAND_PX + FILTER_BAR_PX + PINNED_BAR_PILL_REACH_PX;

/** The insets a game list's menus and on-screen checks use: the chrome, plus the pinned filter while filtering. */
export const listInsets = (phone: boolean, filtering: boolean): ScreenInsets => {
  const chrome = SCREEN_CHROME[phone ? 'phone' : 'desktop'];
  if (!filtering) return chrome;
  return { top: phone ? FILTER_CLEARANCE.phone : FILTER_CLEARANCE.desktop, bottom: chrome.bottom };
};

/** A heading held over the top of a list's scroll area (Game Lists' open list on a wide desktop, its search on its row):
 *  its height and the fade under it, set on that scroll area. A match scrolled to lands under it, and what is under it
 *  is not on screen. Unset elsewhere. */
export const HELD_HEAD_VAR = '--game-list-held-head';

const heldHeadOffset = (el: HTMLElement | null): number =>
  el ? parseFloat(getComputedStyle(el).getPropertyValue(HELD_HEAD_VAR)) || 0 : 0;

/** The pinned area header over a Website area page with the hub on, as the Website shell publishes it; 0 elsewhere. */
const backHeaderOffset = (el: HTMLElement | null): number =>
  el ? parseFloat(getComputedStyle(el).getPropertyValue('--ows-back-header-offset')) || 0 : 0;

/**
 * The band of the screen a list's games count as visible in, measured now: on the page, the chrome (and the
 * pinned filter while filtering); in a dialog, its scroll area (less the pinned filter while filtering, and a heading
 * held over its top).
 */
export const visibleBand = (
  el: HTMLElement | null,
  placement: ListPlacement,
  phone: boolean,
  filtering: boolean,
): ScreenInsets => {
  if (placement === 'page') {
    const insets = listInsets(phone, filtering);
    return { ...insets, top: insets.top + backHeaderOffset(el) };
  }
  const box = el ? scrollParentOf(el)?.getBoundingClientRect() : undefined;
  const top = box ? Math.max(0, box.top) : 0;
  const bottom = box ? Math.max(0, window.innerHeight - box.bottom) : 0;
  return { top: top + heldHeadOffset(el) + (filtering ? DIALOG_FILTER_CLEARANCE : 0), bottom };
};

/** Whether any part of `el` is on screen, between the insets. */
export const isOnScreen = (el: Element, insets: ScreenInsets): boolean => {
  const rect = el.getBoundingClientRect();
  return rect.bottom > insets.top && rect.top < window.innerHeight - insets.bottom;
};
